import {useEffect, useState} from "react";
import {useSearchParams} from "react-router";

import {Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {formatSortingParam, parseSortingParam} from "@/components/table/tableUtils.ts";

export const DEFAULT_SORT_KEY = "sort";

export interface UseSortingOptions {
  sortKey?: string;
  defaultSort?: string;
  useUrlParams?: boolean;
  onSortChange?: (sorting: Sorting, params: URLSearchParams) => void;
}

export const useSorting = ({
  sortKey = DEFAULT_SORT_KEY,
  defaultSort = "name",
  useUrlParams = true,
  onSortChange,
}: UseSortingOptions = {}) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const urlSortValue = useUrlParams ? queryParams.get(sortKey) || defaultSort : defaultSort;
  const [sorting, setSortingState] = useState<Sorting>(() => parseSortingParam(urlSortValue));

  useEffect(() => {
    if (!useUrlParams) {
      return;
    }
    const urlSorting = parseSortingParam(urlSortValue);
    if (sorting.column !== urlSorting.column || sorting.direction !== urlSorting.direction) {
      setSortingState(urlSorting);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sorting intentionally excluded to prevent infinite loops; this effect syncs URL → state
  }, [urlSortValue, useUrlParams]);

  const setSorting = (newSorting: Sorting, externalParams?: URLSearchParams) => {
    const params = externalParams ?? (useUrlParams ? new URLSearchParams(queryParams) : null);
    if (params) {
      params.set(sortKey, formatSortingParam(newSorting));
    }
    onSortChange?.(newSorting, params ?? new URLSearchParams());
    setSortingState(newSorting);
    if (params && !externalParams) {
      setQueryParams(params);
    }
  };

  return {
    sorting,
    setSorting,
    sortingString: sorting.direction == SortingDirection.Asc ? sorting.column : `-${sorting.column}`,
  };
};
